import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SectionBlock from '../../SectionBlock';
import buildQuery from '../../utils/buildQuery';
import { RANGE_DATE } from '@/components/FilterBar/constants/filtersConstants';
import { getOverviewSummary } from '@/store/slices/components/dashboard/service';
import { setMiniCards } from '@/store/slices/components/dashboard/DashboardSlice';
import useVisibility from '@/hooks/useVisibility';
import sortByOrder from '../../utils/sortByOrder';
import Trans from '@/components/shared/Trans';
import translations from '@/components/Dashboard/Dashboard.trans.json';
import { LIST_CLASSNAMES, PROPERTIES_TEXTS, SECTION_TYPES } from './utils';
import {
  MARKETPLACE_REGISTRATION_QUALITY,
  QPRECO_MARKETPLACE,
} from '../../constants';

const tabId = 'overview';
const QUALITY_SCORING_FLAG = 'dashboard_quality_scoring';

function isQualityScoringCard(cardId) {
  return (
    cardId === QPRECO_MARKETPLACE || cardId === MARKETPLACE_REGISTRATION_QUALITY
  );
}

export default function OverviewContent() {
  const dispatch = useDispatch();
  const { edition, miniCards } = useSelector((state) => state.DashboardSlice);
  const {
    loading,
    contactsValue,
    propertiesValue,
    developmentsValue,
    selectedStatusId,
  } = miniCards;
  const { filters } = useSelector((state) => state.FilterBarSlice);
  const { waffle_flags: waffleFlags } = useSelector(
    (state) => state.AuthSlice.permissions
  );
  const isUnivenTheme = useSelector(
    (state) => state.UserSlice.themeConfig.theme === 'univen'
  );
  const hasQualityScoring =
    isUnivenTheme && waffleFlags?.includes(QUALITY_SCORING_FLAG);
  const { isVisible } = useVisibility();
  const tab = edition.tabs?.[tabId];

  useEffect(() => {
    dispatch(setMiniCards({ key: 'loading', value: true }));
  }, [dispatch, filters?.selectedIds, isVisible]);

  useEffect(() => {
    if (!isVisible) return;
    if (edition.active && miniCards.hasFetched) {
      return;
    }

    const filtersBlacklist = [RANGE_DATE];
    const selectedIds = filters?.selectedIds || [];
    const query = buildQuery(selectedIds, filtersBlacklist);

    dispatch(getOverviewSummary(query));
  }, [dispatch, filters?.selectedIds, isVisible]);

  const titlesMap = {
    mini_cards: null,
    CountActiveContacts: Trans(
      translations,
      'CountActiveContacts',
      String(contactsValue)
    ),
    CountAvailableProperties: Trans(
      translations,
      PROPERTIES_TEXTS[selectedStatusId] || 'CountAvailableProperties',
      String(propertiesValue)
    ),
    CountAvailableDevelopments: Trans(
      translations,
      'CountAvailableDevelopments',
      String(developmentsValue)
    ),
  };

  const sections = (tab?.sections ?? [])
    .slice()
    .sort(sortByOrder)
    .map((section) => {
      const withTitle = section.id !== 'mini_cards';
      const listClassName = LIST_CLASSNAMES[section.id];

      const cards = (section.cards ?? [])
        .filter((card) => !isQualityScoringCard(card.id) || hasQualityScoring)
        .slice()
        .sort(sortByOrder);

      return {
        ...section,
        cards,
        title: titlesMap[section.id],
        props: { loading, withTitle },
        type: SECTION_TYPES[section.id] ?? 'card',
        listClassName,
      };
    });

  return sections.map((section) => {
    const { id, title, cards, type, props, visible, order, listClassName } =
      section;

    return (
      <SectionBlock
        key={id}
        tabId={tabId}
        sectionId={id}
        title={title}
        cards={cards}
        order={order}
        type={type}
        titleProps={props}
        visible={visible}
        listClassName={listClassName}
      />
    );
  });
}
